/**
 * Indian numbering: rupees and paise in words.
 *
 * A GST invoice must show the amount in words, and it must be right. Ported
 * from 1.6.x `numberToIndianWords()` (js/app.js:2691-2733) and made a pure
 * function of its input, so it is testable rather than incidental.
 *
 * Ported here are: crore, lakh, thousand, hundred, and the paise form
 * ("... Rupees and Fifty Paise Only").
 */
import type { Money } from './types';

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen',
  'Eighteen', 'Nineteen',
];

const TENS = [
  '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety',
];

function belowThousand(n: number): string {
  if (n < 20) return ONES[n] ?? '';
  if (n < 100) {
    const t = TENS[Math.floor(n / 10)] ?? '';
    const o = n % 10 ? ` ${ONES[n % 10] ?? ''}` : '';
    return `${t}${o}`;
  }
  const h = `${ONES[Math.floor(n / 100)] ?? ''} Hundred`;
  const rest = n % 100;
  return rest ? `${h} ${belowThousand(rest)}` : h;
}

const SCALES: ReadonlyArray<readonly [number, string]> = [
  [1_00_00_000, 'Crore'],
  [1_00_000, 'Lakh'],
  [1_000, 'Thousand'],
];

/** Whole rupees to words. 12345678 → "One Lakh Twenty Three Thousand ...". */
export function rupeesToWords(rupees: number): string {
  if (rupees === 0) return 'Zero';
  const parts: string[] = [];
  let rest = rupees;
  for (const [value, label] of SCALES) {
    const chunk = Math.floor(rest / value);
    if (chunk > 0) {
      parts.push(`${belowThousand(chunk)} ${label}`);
      rest %= value;
    }
  }
  if (rest > 0) parts.push(belowThousand(rest));
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

function paiseToWords(paise: number): string {
  return belowThousand(paise);
}

/**
 * Full amount in words from paise.
 * `895001` → `"Eight Thousand Nine Hundred Fifty Rupees and One Paise Only"`
 */
export function numberToIndianWords(value: Money): string {
  const negative = value < 0;
  const abs = Math.abs(value);
  const rupees = Math.floor(abs / 100);
  const paise = abs % 100;

  // "One Rupee", not "One Rupees". Correct singular matters on a printed
  // invoice, which is the whole point of the field.
  const rupeeLabel = rupees === 1 ? 'Rupee' : 'Rupees';

  const words: string[] = [];
  if (negative) words.push('Minus');
  words.push(rupeesToWords(rupees) || 'Zero', rupeeLabel);
  if (paise > 0) words.push('and', paiseToWords(paise), paise === 1 ? 'Paise' : 'Paise');
  words.push('Only');
  return words.join(' ');
}
